/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useState, useEffect, useCallback } from "react";
import { ModalContext, ModalType } from "@src/components/modals/ModalController";
import { getRelativePath } from "@src/utils/routes";
import { logEvent } from "@src/utils/logClient";
import window from "global/window";
import { useFeatureFlagsWithOverrides } from "@src/utils/useFeatureFlag";
import useSearchParams from "@src/utils/useSearchParams";
import { AppDataContext } from "@src/components/AppDataContext";
import DistrictsHelloBar from "@src/components/Header/DistrictsHelloBar";
import {
  Header as MDSHeader,
  Navbar,
  ExpandingMenu,
  IconApple,
  IconIdentificationBadge,
  IconFolder,
  IconStar,
  IconBook,
  IconUserGroup,
  IconDeviceDesktop,
  IconLock,
  IconNews,
  Button,
} from "@web-monorepo/mds";
import { TranslationContext } from "@src/components/translation/TranslationContext";
import Translate from "@src/components/translation/Translate";
import LoginPart from "../partials/header/LoginPart";
import isMobile from "@src/utils/isMobile";
import { navigate } from "gatsby";
import * as S from "./styles";
import HeaderLanguagePicker from "./HeaderLanguagePicker";

type HeaderProps = {
  showNavBanner: boolean;
  inSchools: boolean;
  hideNav: boolean;
  unpinHeader: boolean;
  closeBannerFunction: () => void;
  handleIsUserInAudience: (newValue: boolean) => void;
  simplifiedHeader?: boolean;
};

const MenuBrandIcon = ({ src }: { src: string }) => {
  return <img src={src} alt="" width={32} height={32} className="h-full object-contain w-full" />;
};

const HeaderComponent: React.FC<HeaderProps> = ({ hideNav, unpinHeader, handleIsUserInAudience, simplifiedHeader }) => {
  const [downloadButtonConfig, setDownloadButtonConfig] = useState<{
    href: string;
    translationPath: string;
  } | null>(null);

  const { showModal } = useContext(ModalContext);
  const showTeacherSignupModal = () => showModal(ModalType.TeacherSignupForm);
  const showSignupModal = () => showModal(ModalType.Signup);

  const teacherSignupParam = useSearchParams("teacher-signup");
  const generalSignupParam = useSearchParams("create-account");
  const modalContext = useContext(ModalContext);
  const featureFlags = useFeatureFlagsWithOverrides();
  const { getIsCountryUS } = useContext(TranslationContext);
  const isCountryUS = getIsCountryUS();

  const {
    data: { loadingSession, type },
  } = useContext(AppDataContext);

  // defining audience for banner
  useEffect(() => {
    if (!loadingSession && type === undefined) {
      handleIsUserInAudience(true);
    }
  }, [loadingSession, type]);

  useEffect(() => {
    if (teacherSignupParam[0] !== null) showTeacherSignupModal();
    if (generalSignupParam[0] !== null) showSignupModal();
  }, []);

  // Detect mobile device and set download button config
  useEffect(() => {
    if (isMobile().apple.tablet) {
      setDownloadButtonConfig({
        href: "https://itunes.apple.com/us/app/classdojo/id552602056?mt=8",
        translationPath: "header.nav.get_ipad_app",
      });
    } else if (isMobile().apple.phone) {
      setDownloadButtonConfig({
        href: "https://itunes.apple.com/us/app/classdojo/id552602056?mt=8",
        translationPath: "header.nav.get_iphone_app",
      });
    } else if (isMobile().android.device) {
      setDownloadButtonConfig({
        href: "https://play.google.com/store/apps/details?id=com.classdojo.android",
        translationPath: "header.nav.get_android_app",
      });
    }
  }, []);

  function openLoginModal() {
    logEvent({ eventValue: window.location.href, eventName: "web.external_page.navbar.log_in.tap" });
    modalContext.showModal(ModalType.Login);
  }

  function openSignupModal() {
    logEvent({
      eventValue: window.location.href,
      eventName: "web.external_page.navbar.sign_up.tap",
      metadata: featureFlags,
    });
    modalContext.showModal(ModalType.Signup);
  }

  // Helper function to handle clicks for links with event tracking and navigation
  const handleNavClick = useCallback((eventName: string, href: string) => {
    return (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      logEvent({
        eventValue: window.location.href,
        eventName,
      });

      // Use Gatsby navigation for internal links, regular navigation for external
      if (href.startsWith("http")) {
        window.location.href = href;
      } else {
        navigate(href);
      }
    };
  }, []);

  // Helper to create translated text node
  const getTranslatedText = (path: string) => {
    return <Translate path={path} />;
  };

  if (simplifiedHeader) {
    return (
      <S.Nav role="navigation" unpinHeader={unpinHeader}>
        <S.HeaderContainer height="100%" simplifiedHeader={simplifiedHeader}>
          <a href={getRelativePath("/")}>
            <S.Logo src="https://static.classdojo.com/img/classdojo-light.svg" alt="ClassDojo" />
          </a>
        </S.HeaderContainer>
      </S.Nav>
    );
  }

  return (
    <>
      <DistrictsHelloBar />
      <MDSHeader>
        <MDSHeader.Logo
          url="https://static.classdojo.com/img/classdojo-logo.svg"
          href={getRelativePath("/")}
          alt="ClassDojo"
          css={{ minWidth: 154 }}
        />

        {!hideNav && (
          <>
            {/* Desktop Navigation */}
            <Navbar className="max-md:hidden">
              {isCountryUS && (
                <>
                  {/* Products Menu */}
                  <Navbar.Menu id="products-menu" label={getTranslatedText("layouts.main.products") as any}>
                    <Navbar.MenuItem
                      label={getTranslatedText("layouts.main.submenu_products_classdojo.cta") as any}
                      description={getTranslatedText("layouts.main.submenu_products_classdojo.description") as any}
                      href="/?utm_source=newnav"
                      onClick={handleNavClick("web.external_page.navbar.products.classdojo.tap", "/?utm_source=newnav")}
                      icon={<MenuBrandIcon src="https://static.classdojo.com/mds/icon-dojo.svg" />}
                    />
                    <Navbar.MenuItem
                      label={getTranslatedText("layouts.main.submenu_products_plus.cta") as any}
                      description={getTranslatedText("layouts.main.submenu_products_plus.description") as any}
                      href="/plus/?utm_source=newnav"
                      onClick={handleNavClick("web.external_page.navbar.products.plus.tap", "/plus/?utm_source=newnav")}
                      icon={<MenuBrandIcon src="https://static.classdojo.com/mds/icon-plus.svg" />}
                    />
                    <Navbar.MenuItem
                      label={getTranslatedText("layouts.main.submenu_products_dojo_islands.cta") as any}
                      description={getTranslatedText("layouts.main.submenu_products_dojo_islands.description") as any}
                      href="/dojo-islands?utm_source=newnav"
                      onClick={handleNavClick(
                        "web.external_page.navbar.products.islands.tap",
                        "/dojo-islands?utm_source=newnav",
                      )}
                      icon={<MenuBrandIcon src="https://static.classdojo.com/mds/icon-islands.svg" />}
                    />
                    <Navbar.MenuItem
                      label={getTranslatedText("layouts.main.submenu_products_dojo_tutor.cta") as any}
                      description={getTranslatedText("layouts.main.submenu_products_dojo_tutor.description") as any}
                      href="https://tutor.classdojo.com/?utm_source=newnav"
                      onClick={handleNavClick(
                        "web.external_page.navbar.products.tutor.tap",
                        "https://tutor.classdojo.com/?utm_source=newnav",
                      )}
                      icon={<MenuBrandIcon src="https://static.classdojo.com/mds/icon-tutor.svg" />}
                    />
                    <Navbar.MenuItem
                      label={getTranslatedText("layouts.main.submenu_products_dojo_sparks.cta") as any}
                      description={getTranslatedText("layouts.main.submenu_products_dojo_sparks.description") as any}
                      href="/sparks?utm_source=newnav"
                      onClick={handleNavClick(
                        "web.external_page.navbar.products.sparks.tap",
                        "/sparks?utm_source=newnav",
                      )}
                      icon={<MenuBrandIcon src="https://static.classdojo.com/mds/icon-sparks.svg" />}
                    />
                  </Navbar.Menu>

                  {/* Schools Menu */}
                  <Navbar.Menu id="schools-menu" label={getTranslatedText("layouts.main.schools") as any}>
                    <Navbar.MenuItem
                      label={getTranslatedText("layouts.main.submenu_schools_teachers.cta") as any}
                      description={getTranslatedText("layouts.main.submenu_schools_teachers.description") as any}
                      href="/teachers/?utm_source=newnav"
                      onClick={handleNavClick(
                        "web.external_page.navbar.schools.teachers.tap",
                        "/teachers/?utm_source=newnav",
                      )}
                      icon={<IconApple />}
                    />
                    <Navbar.MenuItem
                      label={getTranslatedText("layouts.main.submenu_schools_leaders.cta") as any}
                      description={getTranslatedText("layouts.main.submenu_schools_leaders.description") as any}
                      href="/schools/?utm_source=newnav"
                      onClick={handleNavClick(
                        "web.external_page.navbar.schools.leaders.tap",
                        "/schools/?utm_source=newnav",
                      )}
                      icon={<IconIdentificationBadge />}
                    />
                    <Navbar.MenuItem
                      label={getTranslatedText("layouts.main.submenu_schools_resources.cta") as any}
                      description={getTranslatedText("layouts.main.submenu_schools_resources.description") as any}
                      href="/resources/?utm_source=newnav"
                      onClick={handleNavClick(
                        "web.external_page.navbar.schools.resources.tap",
                        "/resources/?utm_source=newnav",
                      )}
                      icon={<IconFolder />}
                    />
                  </Navbar.Menu>

                  {/* Districts Menu */}
                  <Navbar.Menu id="districts-menu" label={getTranslatedText("layouts.main.districts") as any}>
                    <Navbar.MenuItem
                      label={getTranslatedText("layouts.main.submenu_districts_overview.cta") as any}
                      description={getTranslatedText("layouts.main.submenu_districts_overview.description") as any}
                      href="/districts/?utm_source=newnav"
                      onClick={handleNavClick(
                        "web.external_page.navbar.districts.overview.tap",
                        "/districts/?utm_source=newnav",
                      )}
                      icon={<IconStar />}
                    />
                    <Navbar.MenuItem
                      label={getTranslatedText("layouts.main.submenu_districts_roles.cta") as any}
                      description={getTranslatedText("layouts.main.submenu_districts_roles.description") as any}
                      href="/districts/roles/?utm_source=newnav"
                      onClick={handleNavClick(
                        "web.external_page.navbar.districts.roles.tap",
                        "/districts/roles/?utm_source=newnav",
                      )}
                      icon={<IconUserGroup />}
                    />
                    <Navbar.MenuItem
                      label={getTranslatedText("layouts.main.submenu_districts_features.cta") as any}
                      description={getTranslatedText("layouts.main.submenu_districts_features.description") as any}
                      href="/districts/solutions/?utm_source=newnav"
                      onClick={handleNavClick(
                        "web.external_page.navbar.districts.features.tap",
                        "/districts/solutions/?utm_source=newnav",
                      )}
                      icon={<IconDeviceDesktop />}
                    />
                    <Navbar.MenuItem
                      label={getTranslatedText("layouts.main.submenu_districts_privacy.cta") as any}
                      description={getTranslatedText("layouts.main.submenu_districts_privacy.description") as any}
                      href="/privacy-and-security/?utm_source=newnav"
                      onClick={handleNavClick(
                        "web.external_page.navbar.districts.privacy.tap",
                        "/privacy-and-security/?utm_source=newnav",
                      )}
                      icon={<IconLock />}
                    />
                    <Navbar.MenuItem
                      label={getTranslatedText("layouts.main.submenu_districts_stories.cta") as any}
                      description={getTranslatedText("layouts.main.submenu_districts_stories.description") as any}
                      href="/districts/customer-stories/?utm_source=newnav"
                      onClick={handleNavClick(
                        "web.external_page.navbar.districts.stories.tap",
                        "/districts/customer-stories/?utm_source=newnav",
                      )}
                      icon={<IconBook />}
                    />
                    <Navbar.MenuItem
                      label={getTranslatedText("layouts.main.submenu_districts_blog.cta") as any}
                      description={getTranslatedText("layouts.main.submenu_districts_blog.description") as any}
                      href="https://essential.classdojo.com/?utm_source=newnav"
                      onClick={handleNavClick(
                        "web.external_page.navbar.districts.blog.tap",
                        "https://essential.classdojo.com/?utm_source=newnav",
                      )}
                      icon={<IconNews />}
                    />
                  </Navbar.Menu>

                  {/* Families Link */}
                  <Navbar.Link
                    href="/families/"
                    onClick={handleNavClick("web.external_page.navbar.families.tap", "/families/")}
                  >
                    {getTranslatedText("layouts.main.families")}
                  </Navbar.Link>
                </>
              )}

              {!isCountryUS && (
                <>
                  {/* Teachers Link */}
                  <Navbar.Link
                    href={getRelativePath("/teachers/")}
                    onClick={handleNavClick("web.external_page.navbar.teachers.tap", getRelativePath("/teachers/"))}
                  >
                    {getTranslatedText("layouts.main.teachers")}
                  </Navbar.Link>

                  {/* Schools Link */}
                  <Navbar.Link
                    href={getRelativePath("/schools/")}
                    onClick={handleNavClick("web.external_page.navbar.schools.tap", getRelativePath("/schools/"))}
                  >
                    {getTranslatedText("layouts.main.schools")}
                  </Navbar.Link>

                  {/* Resources Menu */}
                  <Navbar.Menu id="resources-menu" label={getTranslatedText("layouts.main.support.resources") as any}>
                    <Navbar.MenuItem
                      label={getTranslatedText("layouts.main.submenu_training.cta") as any}
                      description={getTranslatedText("layouts.main.submenu_training.description") as any}
                      href="/training/?utm_source=newnav"
                      onClick={handleNavClick("web.external_page.navbar.training.tap", "/training/?utm_source=newnav")}
                    />
                    <Navbar.MenuItem
                      label={getTranslatedText("layouts.main.submenu_resources.cta") as any}
                      description={getTranslatedText("layouts.main.submenu_resources.description") as any}
                      href="/resources/?utm_source=newnav"
                      onClick={handleNavClick(
                        "web.external_page.navbar.resources.tap",
                        "/resources/?utm_source=newnav",
                      )}
                    />
                    <Navbar.MenuItem
                      label={getTranslatedText("layouts.main.submenu_big_ideas.cta") as any}
                      description={getTranslatedText("layouts.main.submenu_big_ideas.description") as any}
                      href="https://ideas.classdojo.com/?utm_source=newnav"
                      onClick={handleNavClick(
                        "web.external_page.navbar.big_ideas.tap",
                        "https://ideas.classdojo.com/?utm_source=newnav",
                      )}
                    />
                    <Navbar.MenuItem
                      label={getTranslatedText("layouts.main.submenu_activity_corner.cta") as any}
                      description={getTranslatedText("layouts.main.submenu_activity_corner.description") as any}
                      href="/activity-corner/?utm_source=newnav"
                      onClick={handleNavClick(
                        "web.external_page.navbar.activity_corner.tap",
                        "/activity-corner/?utm_source=newnav",
                      )}
                    />
                    <Navbar.MenuItem
                      label={getTranslatedText("layouts.main.submenu_sl_resources.cta") as any}
                      description={getTranslatedText("layouts.main.submenu_sl_resources.description") as any}
                      href="/schoolleader-resources/?utm_source=newnav"
                      onClick={handleNavClick(
                        "web.external_page.navbar.schoolleader_resources.tap",
                        "/schoolleader-resources/?utm_source=newnav",
                      )}
                    />
                  </Navbar.Menu>
                </>
              )}
            </Navbar>

            {/* Desktop Actions + Mobile Toggle */}
            <MDSHeader.Actions>
              <ul
                css={{ alignItems: "center", listStyle: "none", margin: 0, padding: 0 }}
                className="max-md:hidden flex gap-x-md"
              >
                <HeaderLanguagePicker />
                <LoginPart openLoginModal={openLoginModal} openSignupModal={openSignupModal} />
              </ul>
              {downloadButtonConfig && (
                <div className="max-md:hidden">
                  <div css={{ padding: "15px 10px" }}>
                    <Button
                      variant="primary"
                      size="s"
                      onClick={() => {
                        window.location.href = downloadButtonConfig.href;
                      }}
                    >
                      <Translate path={downloadButtonConfig.translationPath} />
                    </Button>
                  </div>
                </div>
              )}
              <MDSHeader.MobileMenuToggle />
            </MDSHeader.Actions>

            {/* Mobile Menu */}
            <MDSHeader.MobileMenu>
              <div>
                {isCountryUS && (
                  <>
                    {/* Products ExpandingMenu */}
                    <ExpandingMenu value="products">
                      <ExpandingMenu.Trigger>{getTranslatedText("layouts.main.products")}</ExpandingMenu.Trigger>
                      <ExpandingMenu.Content>
                        <ExpandingMenu.Item
                          href="/?utm_source=newnav"
                          onClick={handleNavClick(
                            "web.external_page.navbar.products.classdojo.tap",
                            "/?utm_source=newnav",
                          )}
                        >
                          {getTranslatedText("layouts.main.submenu_products_classdojo.cta")}
                        </ExpandingMenu.Item>
                        <ExpandingMenu.Item
                          href="/plus/?utm_source=newnav"
                          onClick={handleNavClick(
                            "web.external_page.navbar.products.plus.tap",
                            "/plus/?utm_source=newnav",
                          )}
                        >
                          {getTranslatedText("layouts.main.submenu_products_plus.cta")}
                        </ExpandingMenu.Item>
                        <ExpandingMenu.Item
                          href="/dojo-islands?utm_source=newnav"
                          onClick={handleNavClick(
                            "web.external_page.navbar.products.islands.tap",
                            "/dojo-islands?utm_source=newnav",
                          )}
                        >
                          {getTranslatedText("layouts.main.submenu_products_dojo_islands.cta")}
                        </ExpandingMenu.Item>
                        <ExpandingMenu.Item
                          href="https://tutor.classdojo.com/?utm_source=newnav"
                          onClick={handleNavClick(
                            "web.external_page.navbar.products.tutor.tap",
                            "https://tutor.classdojo.com/?utm_source=newnav",
                          )}
                        >
                          {getTranslatedText("layouts.main.submenu_products_dojo_tutor.cta")}
                        </ExpandingMenu.Item>
                        <ExpandingMenu.Item
                          href="/sparks?utm_source=newnav"
                          onClick={handleNavClick(
                            "web.external_page.navbar.products.sparks.tap",
                            "/sparks?utm_source=newnav",
                          )}
                        >
                          {getTranslatedText("layouts.main.submenu_products_dojo_sparks.cta")}
                        </ExpandingMenu.Item>
                      </ExpandingMenu.Content>
                    </ExpandingMenu>

                    {/* Schools ExpandingMenu */}
                    <ExpandingMenu value="schools">
                      <ExpandingMenu.Trigger>{getTranslatedText("layouts.main.schools")}</ExpandingMenu.Trigger>
                      <ExpandingMenu.Content>
                        <ExpandingMenu.Item
                          href="/teachers/?utm_source=newnav"
                          onClick={handleNavClick(
                            "web.external_page.navbar.schools.teachers.tap",
                            "/teachers/?utm_source=newnav",
                          )}
                        >
                          {getTranslatedText("layouts.main.submenu_schools_teachers.cta")}
                        </ExpandingMenu.Item>
                        <ExpandingMenu.Item
                          href="/schools/?utm_source=newnav"
                          onClick={handleNavClick(
                            "web.external_page.navbar.schools.leaders.tap",
                            "/schools/?utm_source=newnav",
                          )}
                        >
                          {getTranslatedText("layouts.main.submenu_schools_leaders.cta")}
                        </ExpandingMenu.Item>
                        <ExpandingMenu.Item
                          href="/resources/?utm_source=newnav"
                          onClick={handleNavClick(
                            "web.external_page.navbar.schools.resources.tap",
                            "/resources/?utm_source=newnav",
                          )}
                        >
                          {getTranslatedText("layouts.main.submenu_schools_resources.cta")}
                        </ExpandingMenu.Item>
                      </ExpandingMenu.Content>
                    </ExpandingMenu>

                    {/* Districts ExpandingMenu */}
                    <ExpandingMenu value="districts">
                      <ExpandingMenu.Trigger>{getTranslatedText("layouts.main.districts")}</ExpandingMenu.Trigger>
                      <ExpandingMenu.Content>
                        <ExpandingMenu.Item
                          href="/districts/?utm_source=newnav"
                          onClick={handleNavClick(
                            "web.external_page.navbar.districts.overview.tap",
                            "/districts/?utm_source=newnav",
                          )}
                        >
                          {getTranslatedText("layouts.main.submenu_districts_overview.cta")}
                        </ExpandingMenu.Item>
                        <ExpandingMenu.Item
                          href="/districts/roles/?utm_source=newnav"
                          onClick={handleNavClick(
                            "web.external_page.navbar.districts.roles.tap",
                            "/districts/roles/?utm_source=newnav",
                          )}
                        >
                          {getTranslatedText("layouts.main.submenu_districts_roles.cta")}
                        </ExpandingMenu.Item>
                        <ExpandingMenu.Item
                          href="/districts/solutions/?utm_source=newnav"
                          onClick={handleNavClick(
                            "web.external_page.navbar.districts.features.tap",
                            "/districts/solutions/?utm_source=newnav",
                          )}
                        >
                          {getTranslatedText("layouts.main.submenu_districts_features.cta")}
                        </ExpandingMenu.Item>
                        <ExpandingMenu.Item
                          href="/privacy-and-security/?utm_source=newnav"
                          onClick={handleNavClick(
                            "web.external_page.navbar.districts.privacy.tap",
                            "/privacy-and-security/?utm_source=newnav",
                          )}
                        >
                          {getTranslatedText("layouts.main.submenu_districts_privacy.cta")}
                        </ExpandingMenu.Item>
                        <ExpandingMenu.Item
                          href="/districts/customer-stories/?utm_source=newnav"
                          onClick={handleNavClick(
                            "web.external_page.navbar.districts.stories.tap",
                            "/districts/customer-stories/?utm_source=newnav",
                          )}
                        >
                          {getTranslatedText("layouts.main.submenu_districts_stories.cta")}
                        </ExpandingMenu.Item>
                        <ExpandingMenu.Item
                          href="https://essential.classdojo.com/?utm_source=newnav"
                          onClick={handleNavClick(
                            "web.external_page.navbar.districts.blog.tap",
                            "https://essential.classdojo.com/?utm_source=newnav",
                          )}
                        >
                          {getTranslatedText("layouts.main.submenu_districts_blog.cta")}
                        </ExpandingMenu.Item>
                      </ExpandingMenu.Content>
                    </ExpandingMenu>

                    {/* Families Link */}
                    <a
                      href="/families/"
                      onClick={handleNavClick("web.external_page.navbar.families.tap", "/families/")}
                      className="w-full flex items-center justify-between cursor-pointer py-20 px-16 transition-colors duration-150 ease-out-quad border-b-1 border-border-decorative text-ginto-23 font-normal leading-130 text-left"
                    >
                      {getTranslatedText("layouts.main.families")}
                    </a>
                  </>
                )}

                {!isCountryUS && (
                  <>
                    {/* Teachers Link */}
                    <a
                      href={getRelativePath("/teachers/")}
                      onClick={handleNavClick("web.external_page.navbar.teachers.tap", getRelativePath("/teachers/"))}
                      className="w-full flex items-center justify-between cursor-pointer py-20 px-16 transition-colors duration-150 ease-out-quad border-b-1 border-border-decorative text-ginto-23 font-normal leading-130 text-left"
                    >
                      {getTranslatedText("layouts.main.teachers")}
                    </a>

                    {/* Schools Link */}
                    <a
                      href={getRelativePath("/schools/")}
                      onClick={handleNavClick("web.external_page.navbar.schools.tap", getRelativePath("/schools/"))}
                      className="w-full flex items-center justify-between cursor-pointer py-20 px-16 transition-colors duration-150 ease-out-quad border-b-1 border-border-decorative text-ginto-23 font-normal leading-130 text-left"
                    >
                      {getTranslatedText("layouts.main.schools")}
                    </a>

                    {/* Resources ExpandingMenu */}
                    <ExpandingMenu value="resources">
                      <ExpandingMenu.Trigger>
                        {getTranslatedText("layouts.main.support.resources")}
                      </ExpandingMenu.Trigger>
                      <ExpandingMenu.Content>
                        <ExpandingMenu.Item
                          href="/training/?utm_source=newnav"
                          onClick={handleNavClick(
                            "web.external_page.navbar.training.tap",
                            "/training/?utm_source=newnav",
                          )}
                        >
                          {getTranslatedText("layouts.main.submenu_training.cta")}
                        </ExpandingMenu.Item>
                        <ExpandingMenu.Item
                          href="/resources/?utm_source=newnav"
                          onClick={handleNavClick(
                            "web.external_page.navbar.resources.tap",
                            "/resources/?utm_source=newnav",
                          )}
                        >
                          {getTranslatedText("layouts.main.submenu_resources.cta")}
                        </ExpandingMenu.Item>
                        <ExpandingMenu.Item
                          href="https://ideas.classdojo.com/?utm_source=newnav"
                          onClick={handleNavClick(
                            "web.external_page.navbar.big_ideas.tap",
                            "https://ideas.classdojo.com/?utm_source=newnav",
                          )}
                        >
                          {getTranslatedText("layouts.main.submenu_big_ideas.cta")}
                        </ExpandingMenu.Item>
                        <ExpandingMenu.Item
                          href="/activity-corner/?utm_source=newnav"
                          onClick={handleNavClick(
                            "web.external_page.navbar.activity_corner.tap",
                            "/activity-corner/?utm_source=newnav",
                          )}
                        >
                          {getTranslatedText("layouts.main.submenu_activity_corner.cta")}
                        </ExpandingMenu.Item>
                        <ExpandingMenu.Item
                          href="/schoolleader-resources/?utm_source=newnav"
                          onClick={handleNavClick(
                            "web.external_page.navbar.schoolleader_resources.tap",
                            "/schoolleader-resources/?utm_source=newnav",
                          )}
                        >
                          {getTranslatedText("layouts.main.submenu_sl_resources.cta")}
                        </ExpandingMenu.Item>
                      </ExpandingMenu.Content>
                    </ExpandingMenu>
                  </>
                )}
              </div>

              <MDSHeader.MobileMenuFooter>
                <MDSHeader.Actions className="flex flex-col gap-y-16">
                  <div className="w-full">
                    <HeaderLanguagePicker variant="mobile" />
                  </div>
                  <div
                    css={{
                      display: "flex",
                      alignItems: "center",
                      listStyle: "none",
                      margin: 0,
                      gap: 16,
                      padding: 0,
                      width: "100%",
                    }}
                  >
                    <LoginPart openLoginModal={openLoginModal} openSignupModal={openSignupModal} />
                  </div>
                  <div css={{ width: "100%", display: "flex" }}>
                    {downloadButtonConfig && (
                      <Button
                        variant="secondary"
                        size="s"
                        className="grow md:grow-0"
                        onClick={() => {
                          window.location.href = downloadButtonConfig.href;
                        }}
                      >
                        <Translate path={downloadButtonConfig.translationPath} />
                      </Button>
                    )}
                  </div>
                </MDSHeader.Actions>
              </MDSHeader.MobileMenuFooter>
            </MDSHeader.MobileMenu>
          </>
        )}
      </MDSHeader>
    </>
  );
};

export default HeaderComponent;
